import { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Scale, Edit3, Loader2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import Dropdown from './Dropdown';
import { SECTION_COLOR } from './receptUtils';

/**
 * Správa jednotek položky — základní jednotka + sekundární jednotky s převodním
 * koeficientem. Sdílí ji receptura i karty materiálu (suroviny, obaly,
 * spotřební zboží); liší se jen endpointy.
 *
 * Základní jednotka se ukládá s položkou (přes její tlačítko Uložit), sekundární
 * jednotky se ukládají rovnou — a po uložení se modal zavře.
 *
 * listUrl — GET (seznam) / POST (nová sekundární jednotka)
 * itemUrl — (id) => URL pro PUT (úprava) / DELETE (smazání)
 */
export default function JednotkyModal({
    open, onClose, isDark,
    jednotky = [],
    jednotkaId, onJednotkaChange,
    altJednotky = [], onAltJednotkyChange,
    api, listUrl, itemUrl,
    isNew = false,
    novaHint = 'Nejdříve uložte položku.',
}) {
    // { id?, jednotka_do_id, koeficient } — s id jde o úpravu existující jednotky
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Zavřený modal nedrží rozdělaný formulář ani starou chybu
    useEffect(() => { if (!open) { setForm(null); setError(''); } }, [open]);

    if (!open) return null;

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted, #94a3b8)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', borderRadius: 6, padding: '0 12px', fontSize: 13,
        width: '100%', height: 38, outline: 'none', colorScheme: isDark ? 'dark' : 'light',
    };
    const btnPrimary = {
        background: SECTION_COLOR, color: '#fff', border: 'none', borderRadius: 6,
        padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
    };
    const btnSecondary = {
        background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--surface-base)',
        color: secondaryColor, border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 500,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
    };

    const zakladniZkratka = jednotky.find(j => j.id === jednotkaId)?.zkratka || 'zákl. jedn.';
    const altId = (a) => a.jednotka_do_id ?? a.jednotka_do?.id;
    // Nabízíme jen jednotky, které ještě nejsou obsazené — a nikdy tu základní.
    const obsazene = new Set(altJednotky.filter(a => a.id !== form?.id).map(altId));
    const altOptions = jednotky
        .filter(j => j.id !== jednotkaId && !obsazene.has(j.id))
        .map(j => ({ value: j.id, label: `${j.nazev} (${j.zkratka})` }));

    const prenacti = async () => onAltJednotkyChange(await api(listUrl));

    const ulozAlt = async () => {
        if (!form?.jednotka_do_id || !form?.koeficient) { setError('Vyplňte jednotku i koeficient.'); return; }
        setSaving(true); setError('');
        try {
            const body = { jednotka_do_id: form.jednotka_do_id, koeficient: form.koeficient };
            if (form.id) await api(itemUrl(form.id), 'PUT', body);
            else await api(listUrl, 'POST', body);
            setForm(null);
            await prenacti();
            onClose();
        } catch (e) {
            setError(e.error || e.message || 'Chyba při ukládání');
        }
        setSaving(false);
    };

    const smazAlt = async (id) => {
        if (!confirm('Smazat sekundární jednotku?')) return;
        setError('');
        try {
            await api(itemUrl(id), 'DELETE');
            if (form?.id === id) setForm(null);
            await prenacti();
        } catch (e) { setError(e.error || e.message || 'Nelze smazat'); }
    };

    return (
        <ModalShell onClose={onClose} isDark={isDark} ikona={Scale} barva={SECTION_COLOR} nadpis="Jednotky">
                <div className="p-4 space-y-4">
                    {/* Základní jednotka */}
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: mutedColor }}>Základní jednotka *</p>
                        <Dropdown
                            value={jednotkaId || ''}
                            onChange={v => onJednotkaChange(v ? parseInt(v) : '')}
                            options={jednotky.map(j => ({ value: j.id, label: `${j.nazev} (${j.zkratka})` }))}
                            placeholder="— vyberte —" isDark={isDark} accent={SECTION_COLOR} />
                        <p className="text-[10px] mt-1" style={{ color: mutedColor }}>Určuje jednotku kalkulace. Uloží se s položkou přes tlačítko Uložit.</p>
                    </div>

                    {/* Sekundární jednotky */}
                    <div className="pt-3" style={{ borderTop: `1px solid ${borderColor}` }}>
                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: mutedColor }}>Sekundární jednotky</p>
                        {isNew ? (
                            <p className="text-xs" style={{ color: mutedColor }}>{novaHint}</p>
                        ) : (<>
                            {altJednotky.length > 0 && (
                                <div className="rounded-xl overflow-hidden mb-3" style={{ border: `1px solid ${borderColor}` }}>
                                    {altJednotky.map((a, idx) => (
                                        <div key={a.id} className="flex items-center gap-2 px-3 py-2.5" style={{
                                            borderBottom: idx < altJednotky.length - 1 ? `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-default)'}` : 'none',
                                            background: form?.id === a.id ? 'rgba(245,158,11,0.10)' : 'transparent',
                                        }}>
                                            <span className="text-sm flex-1 min-w-0 truncate" style={{ color: 'var(--text-primary)' }}>
                                                {a.jednotka_z?.zkratka || '?'} <span style={{ color: mutedColor }}>→</span> {a.jednotka_do?.zkratka || '?'}
                                            </span>
                                            <span className="text-xs px-2 py-0.5 rounded-md" style={{ fontFamily: 'JetBrains Mono, monospace', color: secondaryColor, background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-base)' }}>× {a.koeficient}</span>
                                            <button onClick={() => { setError(''); setForm({ id: a.id, jednotka_do_id: altId(a), koeficient: a.koeficient }); }} title="Upravit"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2 }}
                                                onMouseEnter={e => e.currentTarget.style.color = SECTION_COLOR} onMouseLeave={e => e.currentTarget.style.color = mutedColor}><Edit3 size={13} /></button>
                                            <button onClick={() => smazAlt(a.id)} title="Smazat"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 2 }}
                                                onMouseEnter={e => e.currentTarget.style.color = '#EF4444'} onMouseLeave={e => e.currentTarget.style.color = mutedColor}><Trash2 size={13} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {form ? (
                                <div className="rounded-xl p-4 mb-3" style={{ border: `1px solid ${borderColor}` }}>
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div>
                                            <label className="block text-xs font-medium mb-1" style={{ color: secondaryColor }}>Na jednotku</label>
                                            <Dropdown value={form.jednotka_do_id || ''} onChange={v => setForm({ ...form, jednotka_do_id: v ? parseInt(v) : '' })}
                                                options={altOptions} placeholder="—" isDark={isDark} accent={SECTION_COLOR} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1" style={{ color: secondaryColor }}>Koeficient</label>
                                            <input type="number" step="0.000001" value={form.koeficient ?? ''}
                                                onChange={e => setForm({ ...form, koeficient: e.target.value })}
                                                placeholder={`1 jednotka = ? ${zakladniZkratka}`} style={inputStyle} />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={ulozAlt} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                                            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Uložit
                                        </button>
                                        <button onClick={() => { setForm(null); setError(''); }} style={btnSecondary}>Zrušit</button>
                                    </div>
                                </div>
                            ) : (
                                <button onClick={() => { setError(''); setForm({ jednotka_do_id: '', koeficient: '' }); }} style={btnPrimary}>
                                    <Plus size={14} /> Přidat sek. jednotku
                                </button>
                            )}
                        </>)}
                        {error && <p className="text-xs mt-2" style={{ color: '#EF4444' }}>{error}</p>}
                    </div>
                </div>
        </ModalShell>
    );
}
